import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NextAction, PostPatch } from '@cortex/domain-team-meeting';
import { teamMeetingsApi } from '../lib/api.js';
import type { PostRef, SeriesCreateRequest } from '../lib/api/team-meetings.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * 見られる週次MTG のシリーズの一覧。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 自分が見られる週次MTG のシリーズを、区分の候補とできることつきで取得する
 * @graph-connects /team-meetings/series [calls] シリーズ一覧API
 * @graph-connects teamMeetingsApi.listSeries [calls] 週次MTG APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useTeamMeetingSeriesList() {
  return useQuery({
    queryKey: queryKeys.teamMeetings.series(),
    queryFn: () => teamMeetingsApi.listSeries(),
  });
}

/**
 * シリーズ 1 件（開催回の一覧と今の開催回の日付）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG のシリーズの設定・開催回の一覧・今投稿を集めている開催回の日付を取得する
 * @graph-connects /team-meetings/series/{seriesId} [calls] シリーズ詳細API
 * @graph-connects teamMeetingsApi.getSeries [calls] 週次MTG APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useTeamMeetingSeries(seriesId: string) {
  return useQuery({
    queryKey: queryKeys.teamMeetings.seriesDetail(seriesId),
    queryFn: () => teamMeetingsApi.getSeries(seriesId),
  });
}

/**
 * 開催回（まだ棚卸ししていない投稿・その回で棚卸しした投稿・持ち越しの Next）。開催回が決まるまでは取らない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG のまだ棚卸ししていない投稿・その開催回で棚卸しした投稿・持ち越しの Next を取得する
 * @graph-connects /team-meetings/series/{seriesId}/meetings/{meetingId} [calls] 開催回API
 * @graph-connects teamMeetingsApi.getMeeting [calls] 週次MTG APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useTeamMeeting(seriesId: string, meetingId: string | null) {
  return useQuery({
    queryKey: queryKeys.teamMeetings.meeting(seriesId, meetingId ?? ''),
    queryFn: meetingId === null ? skipToken : () => teamMeetingsApi.getMeeting(seriesId, meetingId),
  });
}

/**
 * 開催回の定例の自動ブロック。**投稿とは別に取る**（BigQuery を引くので遅い。投稿の表示を待たせない）。
 * 失敗しても他の表示は止めない（呼び手がブロックの場所にだけ失敗を出す）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の開催回の自動ブロック（KPI・休日オペ担・案件）を投稿とは別に取得する
 * @graph-connects /team-meetings/series/{seriesId}/meetings/{meetingId}/auto-blocks [calls] 自動ブロックAPI
 * @graph-connects teamMeetingsApi.getAutoBlocks [calls] 週次MTG APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useTeamMeetingAutoBlocks(seriesId: string, meetingId: string | null) {
  return useQuery({
    queryKey: queryKeys.teamMeetings.autoBlocks(seriesId, meetingId ?? ''),
    queryFn:
      meetingId === null ? skipToken : () => teamMeetingsApi.getAutoBlocks(seriesId, meetingId),
    // 数字は日中に何度も変わるものではない。開き直すたびに BigQuery を叩かない
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/**
 * 書き込みの後に週次MTG の表示を読み直す（棚卸しで開催回が新しく作られるなど、どこが変わるかを
 * 書き手が知らなくてよいように全体を読み直す。自動ブロックは書き込みで変わらないので除く）。
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
function useRefreshTeamMeetings() {
  const client = useQueryClient();
  return () =>
    client.invalidateQueries({
      queryKey: queryKeys.teamMeetings.all,
      predicate: (query) => query.queryKey[1] !== 'autoBlocks',
    });
}

/**
 * シリーズを作る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG のシリーズを所有組織・公開範囲・既定のボードを指定して作る
 * @graph-connects /team-meetings/series [calls] シリーズ作成API
 * @graph-connects teamMeetingsApi.createSeries [calls] 週次MTG APIクライアント呼び出し
 * @graph-connects useRefreshTeamMeetings [calls] 作成後の再取得
 */
export function useCreateTeamMeetingSeries() {
  const refresh = useRefreshTeamMeetings();
  return useMutation({
    mutationFn: (input: SeriesCreateRequest) => teamMeetingsApi.createSeries(input),
    onSuccess: refresh,
  });
}

/**
 * 投稿する（開催回は指定しない。次の MTG で棚卸しされる）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG に区分と本文を指定していつでも投稿する
 * @graph-connects /team-meetings/series/{seriesId}/posts [calls] 投稿API
 * @graph-connects teamMeetingsApi.createPost [calls] 週次MTG APIクライアント呼び出し
 * @graph-connects useRefreshTeamMeetings [calls] 投稿後の再取得
 */
export function useCreateTeamMeetingPost(seriesId: string) {
  const refresh = useRefreshTeamMeetings();
  return useMutation({
    mutationFn: (input: { category: string; body: string }) =>
      teamMeetingsApi.createPost(seriesId, input),
    onSuccess: refresh,
  });
}

/**
 * 投稿の本文・区分・議論メモを書き換える。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の投稿の本文・区分（本人だけ）と議論メモ（見られる人）を書き換える
 * @graph-connects /team-meetings/series/{seriesId}/posts/{postId} [calls] 投稿更新API
 * @graph-connects teamMeetingsApi.updatePost [calls] 週次MTG APIクライアント呼び出し
 * @graph-connects useRefreshTeamMeetings [calls] 更新後の再取得
 */
export function useUpdateTeamMeetingPost() {
  const refresh = useRefreshTeamMeetings();
  return useMutation({
    mutationFn: ({ ref, patch }: { ref: PostRef; patch: PostPatch }) =>
      teamMeetingsApi.updatePost(ref, patch),
    onSuccess: refresh,
  });
}

/**
 * 自分の投稿を消す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の自分の投稿を消す
 * @graph-connects /team-meetings/series/{seriesId}/posts/{postId} [calls] 投稿削除API
 * @graph-connects teamMeetingsApi.deletePost [calls] 週次MTG APIクライアント呼び出し
 * @graph-connects useRefreshTeamMeetings [calls] 削除後の再取得
 */
export function useDeleteTeamMeetingPost() {
  const refresh = useRefreshTeamMeetings();
  return useMutation({
    mutationFn: (ref: PostRef) => teamMeetingsApi.deletePost(ref),
    onSuccess: refresh,
  });
}

/**
 * いいねを付け外しする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の投稿への自分のいいねを付け外しする
 * @graph-connects /team-meetings/series/{seriesId}/posts/{postId}/like [calls] いいねAPI
 * @graph-connects teamMeetingsApi.toggleLike [calls] 週次MTG APIクライアント呼び出し
 * @graph-connects useRefreshTeamMeetings [calls] 更新後の再取得
 */
export function useToggleTeamMeetingLike() {
  const refresh = useRefreshTeamMeetings();
  return useMutation({
    mutationFn: (ref: PostRef) => teamMeetingsApi.toggleLike(ref),
    onSuccess: refresh,
  });
}

/**
 * 投稿のネクストアクションを決めて棚卸しする（リーダー以上）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の投稿をチーム課題・改善要望・対応を書いてクローズのどれかに決めて棚卸しする
 * @graph-connects /team-meetings/series/{seriesId}/posts/{postId}/triage [calls] 棚卸しAPI
 * @graph-connects teamMeetingsApi.triage [calls] 週次MTG APIクライアント呼び出し
 * @graph-connects useRefreshTeamMeetings [calls] 棚卸し後の再取得
 */
export function useTriageTeamMeetingPost() {
  const refresh = useRefreshTeamMeetings();
  return useMutation({
    mutationFn: ({ ref, nextAction }: { ref: PostRef; nextAction: NextAction }) =>
      teamMeetingsApi.triage(ref, nextAction),
    onSuccess: refresh,
  });
}

/**
 * 棚卸しを戻す（リーダー以上）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の棚卸し済みの投稿を、まだ棚卸ししていない状態に戻す
 * @graph-connects /team-meetings/series/{seriesId}/posts/{postId}/triage [calls] 棚卸しを戻すAPI
 * @graph-connects teamMeetingsApi.reopen [calls] 週次MTG APIクライアント呼び出し
 * @graph-connects useRefreshTeamMeetings [calls] 戻した後の再取得
 */
export function useReopenTeamMeetingPost() {
  const refresh = useRefreshTeamMeetings();
  return useMutation({
    mutationFn: (ref: PostRef) => teamMeetingsApi.reopen(ref),
    onSuccess: refresh,
  });
}
